/**
 * GlobalSidebar - Navigation-only sidebar (Claude/Google pattern)
 * 
 * Features:
 * - Collapsible (icon-only ↔ full width)
 * - Progressive disclosure (Agents expands to show list)
 * - Wayfinder breadcrumbs integration
 * - Navigation groups: Core, Insights
 * 
 * Actions (New Agent, Refine, Tools) are now in GlobalHeader
 * 
 * Brand: Playfair Display, 700ms transitions, premium minimalism
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Layers,
  Settings,
  Home,
  ChevronDown,
  ChevronLeft,
  Menu,
  Search,
  Building2, 
  Users, 
  Briefcase, 
  TrendingUp, 
  Zap,
  X,
  Send,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSearchPath } from '../../contexts/SearchPathContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import api from '../../utils/axios';

export default function GlobalSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { creditBalance, orgPlanType, planInfo } = useSubscription();
  const { path: searchPath, canGoBack, goBack, goToStep } = useSearchPath();
  const { isMobileSidebarOpen, closeMobileSidebar } = useNavigation();
  const isMobile = useIsMobile();
  
  // Sidebar state - default to collapsed (icons-only) on desktop
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    core: true,
    insights: true,
  });
  const [expandedAgents, setExpandedAgents] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [inReachBannerDismissed, setInReachBannerDismissed] = useState(
    () => localStorage.getItem('eqinreach_banner_dismissed') === '1'
  );

  
  // Effective collapsed state - expands on hover when collapsed (desktop only)
  // On mobile, sidebar is always expanded when open
  const effectiveCollapsed = isMobile ? false : (isCollapsed && !isHovered);

  // Fetch agents when expanded
  useEffect(() => {
    if (expandedAgents && agents.length === 0 && isAuthenticated) {
      fetchAgents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedAgents, isAuthenticated]);

  const [teamAgents, setTeamAgents] = useState([]);

  const fetchAgents = async () => {
    setAgentsLoading(true);
    try {
      const response = await api.get('/api/saved-searches');
      if (response.data?.savedSearches) {
        setAgents(response.data.savedSearches);
      }
      if (response.data?.teamSearches?.length > 0) {
        setTeamAgents(response.data.teamSearches);
      }
    } catch (error) {
      console.warn('Failed to fetch agents:', error.message);
    } finally {
      setAgentsLoading(false);
    }
  };

  // Listen for agent updates
  useEffect(() => {
    const handleUpdate = () => {
      if (expandedAgents) fetchAgents();
    };
    window.addEventListener('eq:agent-updated', handleUpdate);
    return () => window.removeEventListener('eq:agent-updated', handleUpdate);
  }, [expandedAgents]);

  // Calculate sidebar width based on effective collapsed state
  const sidebarWidth = effectiveCollapsed ? 64 : 240;

  // Set CSS variable for content spacing (must be before early return)
  // On mobile, sidebar width should be 0 as it overlays content
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isMobile ? '0px' : `${sidebarWidth}px`);
  }, [sidebarWidth, isMobile]);
  
  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isMobileSidebarOpen]);

  // Close expanded agents when route changes (must be before early return)
  useEffect(() => {
    setExpandedAgents(false);
  }, [location.pathname]);

  // Don't show on public pages or when not authenticated
  const isPublicPage = location.pathname.startsWith('/share') || 
                       location.pathname.startsWith('/legal') ||
                       location.pathname.startsWith('/learn') ||
                       location.pathname === '/';
  
  if (isPublicPage || !isAuthenticated) return null;
  
  // On mobile, don't render if sidebar is closed
  if (isMobile && !isMobileSidebarOpen) return null;

  const agentCount = agents.length;
  const filteredAgents = agents.filter(agent => 
    !agentSearchQuery || 
    agent.name?.toLowerCase().includes(agentSearchQuery.toLowerCase())
  );

  // Navigation items - only ONE item highlighted at a time
  const isOnFeed = location.pathname.startsWith('/app/feed');
  const isOnSettings = location.pathname.startsWith('/app/settings');
  const isOnReach = location.pathname.startsWith('/app/reach');
  const isOnHome = location.pathname === '/app' && !isOnFeed && !isOnSettings && !isOnReach;

  const navItems = [
    {
      id: 'core',
      label: 'Core',
      items: [
        { 
          id: 'home', 
          icon: Home, 
          label: 'Home', 
          path: '/app',
          active: isOnHome
        },
        { 
          id: 'agents', 
          icon: Layers, 
          label: 'Agents', 
          count: agentCount,
          expandable: true,
          active: isOnFeed
        },
        {
          id: 'reach',
          icon: Send,
          label: 'Reach',
          path: '/app/reach',
          active: isOnReach
        },
      ],
    },
    {
      id: 'insights',
      label: 'Insights',
      items: [
        { 
          id: 'settings', 
          icon: Settings, 
          label: 'Settings', 
          path: '/app/settings',
          active: isOnSettings
        },
      ],
    },
  ];

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleNavClick = (item) => {
    // If collapsed and clicking Agents, expand sidebar first
    if (effectiveCollapsed && item.expandable && item.id === 'agents') {
      setIsCollapsed(false);
      setTimeout(() => {
        setExpandedAgents(true);
        fetchAgents();
      }, 300); // Wait for sidebar expansion animation
      return;
    }
    
    if (item.expandable && item.id === 'agents') {
      setExpandedAgents(!expandedAgents);
      if (!expandedAgents) {
        fetchAgents();
      }
      return;
    }
    
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }
    
    // Close mobile sidebar after navigation
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleAgentSelect = (agent) => {
    navigate(`/app/feed/${agent.id}`);
    setExpandedAgents(false);
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  // Get icon for wayfinder step source type
  const getSourceIcon = (sourceType) => {
    switch (sourceType) {
      case 'company': return Building2;
      case 'person': return Users;
      case 'job': return Briefcase;
      case 'news': return TrendingUp;
      case 'skill': return Zap;
      case 'industry': return Building2;
      default: return Home;
    }
  };

  // Format step label for wayfinder
  const formatStepLabel = (step) => {
    if (step.source?.name) {
      return step.source.name;
    }
    const query = step.query || 'Search';
    return query.length > 12 ? query.slice(0, 10) + '...' : query;
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isMobile && isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}
      
      <aside 
        className={`fixed left-0 top-0 bottom-0 z-[9999] flex flex-col bg-white border-r border-black/5 transition-all duration-300 ease-out ${
          isMobile 
            ? 'w-72 shadow-2xl' 
            : effectiveCollapsed ? 'w-16' : 'w-60'
        }`}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
        {/* Header - Collapse Toggle (desktop) or Close Button (mobile) */}
        <div className="h-14 px-3 border-b border-black/5 flex items-center justify-between flex-shrink-0">
          {isMobile ? (
            <>
              {/* Mobile: Logo + Close */}
              <div className="flex items-center gap-2">
                <img 
                  src="/favicon/favicon.svg" 
                  alt="EQ.app" 
                  className="w-7 h-7 object-contain"
                />
                <span 
                  className="text-sm font-semibold text-black tracking-tight flex items-center gap-1.5"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Scout
                  {process.env.REACT_APP_SCOUT_EXPERIMENTAL === 'true' && (
                    <span className="text-[10px] font-medium text-[#5a20ff] bg-[#5a20ff]/10 px-1.5 py-0.5 rounded">
                      Experimental
                    </span>
                  )}
                </span>
              </div>
              <button
                onClick={closeMobileSidebar}
                className="p-2 rounded-lg hover:bg-black/5 active:bg-black/10 transition-colors duration-200"
                aria-label="Close menu"
              >
                <X className="w-5 h-5 text-black/50" />
              </button>
            </>
          ) : (
            /* Desktop: Collapse Toggle */
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 rounded-lg hover:bg-black/5 transition-colors duration-200 mx-auto"
              title={isCollapsed ? 'Pin sidebar open' : 'Collapse sidebar'}
            >
              {effectiveCollapsed ? (
                <Menu className="w-5 h-5 text-black/50" />
              ) : (
                <ChevronLeft className="w-5 h-5 text-black/50" />
              )}
            </button>
          )}
        </div>

      {/* Wayfinder Breadcrumbs - Compact vertical version */}
      {!effectiveCollapsed && searchPath.length > 0 && (
        <div className="px-3 py-2 border-b border-black/5">
          <div className="flex items-center gap-1 mb-1">
            <button
              onClick={goBack}
              disabled={!canGoBack}
              className={`p-1 rounded transition-colors duration-200 ${
                canGoBack 
                  ? 'hover:bg-[#5a20ff]/10 text-[#5a20ff]' 
                  : 'text-black/20 cursor-not-allowed'
              }`}
              title="Go back"
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className="text-[10px] uppercase tracking-wide text-black/40 flex-1">
              Path
            </span>
          </div>
          <div className="flex flex-wrap gap-1 max-h-[60px] overflow-y-auto">
            {searchPath.slice(-3).map((step) => {
              const Icon = getSourceIcon(step.source?.type);
              return (
                <button
                  key={step.id}
                  onClick={() => goToStep(step.id)}
                  className="flex items-center gap-1 px-1.5 py-0.5 bg-[#5a20ff]/10 text-[#5a20ff] rounded text-[10px] hover:bg-[#5a20ff]/20 transition-colors duration-200"
                  title={step.query}
                >
                  <Icon className="w-2.5 h-2.5" />
                  <span className="truncate max-w-[60px]">{formatStepLabel(step)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {navItems.map((group) => {
          const isGroupExpanded = expandedGroups[group.id];
          
          return (
            <div key={group.id} className="mb-1">
              {/* Group Header */}
              {!effectiveCollapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center gap-2 px-4 py-1.5 text-[10px] uppercase tracking-wider text-black/40 hover:text-black/60 transition-colors duration-200"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  <ChevronDown 
                    className={`w-3 h-3 transition-transform duration-200 ${
                      isGroupExpanded ? '' : '-rotate-90'
                    }`} 
                  />
                  {group.label}
                </button>
              )}
              
              {/* Group Items */}
              {isGroupExpanded && group.items.map((item) => {
                const Icon = item.icon;
                const isExpanded = item.id === 'agents' && expandedAgents;
                
                return (
                  <div key={item.id}>
                    {/* Navigation Item */}
                    <button
                      onClick={() => handleNavClick(item)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-200 ${
                        item.active 
                          ? 'bg-[#5a20ff]/10 text-[#5a20ff] border-r-2 border-[#5a20ff]' 
                          : 'text-black/60 hover:bg-black/5 hover:text-black'
                      } ${effectiveCollapsed ? 'justify-center px-2 py-3' : ''}`}
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      title={effectiveCollapsed ? `${item.label}${item.count ? ` (${item.count})` : ''}` : undefined}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {!effectiveCollapsed && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          {item.count !== undefined && item.count > 0 && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-black/10 rounded-full">
                              {item.count}
                            </span>
                          )}
                          {item.expandable && (
                            <ChevronDown 
                              className={`w-4 h-4 text-black/30 transition-transform duration-200 ${
                                isExpanded ? 'rotate-180' : ''
                              }`} 
                            />
                          )}
                        </>
                      )}
                    </button>

                    {/* Agents List - Progressive Disclosure */}
                    {!effectiveCollapsed && item.id === 'agents' && isExpanded && (
                      <div className="ml-4 mr-2 mb-2 border-l-2 border-[#5a20ff]/20 pl-2">
                        {/* Search */}
                        <div className="relative mb-2">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/40" />
                          <input
                            type="text"
                            value={agentSearchQuery}
                            onChange={(e) => setAgentSearchQuery(e.target.value)}
                            placeholder="Search agents..."
                            className="w-full pl-8 pr-2 py-1.5 bg-white border border-black/10 rounded-lg text-xs text-black placeholder-black/40 focus:outline-none focus:border-[#5a20ff]/30 transition-all duration-200"
                          />
                        </div>

                        {/* My Agents List */}
                        <div className="max-h-[250px] overflow-y-auto space-y-0.5">
                          {agentsLoading ? (
                            <div className="px-2 py-4 text-center text-xs text-black/40">
                              Loading...
                            </div>
                          ) : filteredAgents.length === 0 ? (
                            <div className="px-2 py-4 text-center text-xs text-black/40">
                              {agentSearchQuery ? 'No matches' : 'No agents yet'}
                            </div>
                          ) : (
                            filteredAgents.map((agent) => {
                              const isSelected = location.pathname === `/app/feed/${agent.id}`;
                              const isOrgShared = agent.visibility === 'org';
                              return (
                                <button
                                  key={agent.id}
                                  onClick={() => handleAgentSelect(agent)}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors duration-200 ${
                                    isSelected
                                      ? 'bg-[#5a20ff]/10 text-[#5a20ff]'
                                      : 'text-black/60 hover:bg-black/5 hover:text-black'
                                  }`}
                                >
                                  <div className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                                  <span className="flex-1 text-left truncate">{agent.name || 'Unnamed Agent'}</span>
                                  {isOrgShared && (
                                    <Users className="w-2.5 h-2.5 text-[#52dcb3] flex-shrink-0" title="Shared with workspace" />
                                  )}
                                  {agent.leads_count > 0 && (
                                    <span className="text-[10px] text-black/40">
                                      {agent.leads_count}
                                    </span>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {/* Team Searches */}
                        {teamAgents.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-black/5">
                            <div className="flex items-center gap-1.5 px-2 mb-1.5">
                              <Users className="w-3 h-3 text-[#52dcb3]" />
                              <span className="text-[10px] font-semibold text-black/40 uppercase tracking-wide">Team Searches</span>
                            </div>
                            <div className="max-h-[150px] overflow-y-auto space-y-0.5">
                              {teamAgents
                                .filter(a => !agentSearchQuery || a.name?.toLowerCase().includes(agentSearchQuery.toLowerCase()))
                                .map((agent) => {
                                  const isSelected = location.pathname === `/app/feed/${agent.id}`;
                                  return (
                                    <button
                                      key={`team-${agent.id}`}
                                      onClick={() => handleAgentSelect(agent)}
                                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors duration-200 ${
                                        isSelected
                                          ? 'bg-[#52dcb3]/15 text-[#52dcb3]'
                                          : 'text-black/50 hover:bg-black/5 hover:text-black/70'
                                      }`}
                                    >
                                      <Users className="w-2.5 h-2.5 opacity-50 flex-shrink-0" />
                                      <span className="flex-1 text-left truncate">{agent.name || 'Unnamed'}</span>
                                      {agent.shared_by_name && (
                                        <span className="text-[9px] text-black/30 truncate max-w-[50px]">{agent.shared_by_name.split(' ')[0]}</span>
                                      )}
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

        {/* EQ inReach Banner */}
        {!inReachBannerDismissed && !effectiveCollapsed && (
          <div className="flex-shrink-0 mx-3 mb-2 relative group">
            <a
              href="https://www.eqenrich.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-3 rounded-xl bg-gradient-to-br from-[#5a20ff]/10 to-[#52dcb3]/10 hover:from-[#5a20ff]/15 hover:to-[#52dcb3]/15 border border-[#5a20ff]/10 transition-all"
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-[#5a20ff]" />
                <span className="text-xs font-semibold text-[#5a20ff]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  EQ Enrich
                </span>
                <ExternalLink className="w-3 h-3 text-[#5a20ff]/50 ml-auto" />
              </div>
              <p className="text-[10px] text-black/50 leading-relaxed">
                Try our new platform
              </p>
            </a>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                localStorage.setItem('eqinreach_banner_dismissed', '1');
                setInReachBannerDismissed(true);
              }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-black/10 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-black/40" />
            </button>
          </div>
        )}
        {!inReachBannerDismissed && effectiveCollapsed && (
          <div className="flex-shrink-0 px-2 mb-2">
            <a
              href="https://www.eqenrich.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-gradient-to-br from-[#5a20ff]/10 to-[#52dcb3]/10 hover:from-[#5a20ff]/15 hover:to-[#52dcb3]/15 transition-all"
              title="Try EQ inReach"
            >
              <Sparkles className="w-4 h-4 text-[#5a20ff]" />
            </a>
          </div>
        )}

        {/* Credit Balance Badge */}
        <div className="flex-shrink-0 border-t border-black/5 p-3">
          {effectiveCollapsed ? (
            <button
              onClick={() => navigate('/app/settings?tab=workspace')}
              className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-gradient-to-br from-[#5a20ff]/10 to-[#52dcb3]/10 hover:from-[#5a20ff]/20 hover:to-[#52dcb3]/20 transition-all"
              title={`${creditBalance} credits`}
            >
              <Zap className="w-4 h-4 text-[#5a20ff]" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/app/settings?tab=workspace')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 hover:from-[#5a20ff]/10 hover:to-[#52dcb3]/10 transition-all group"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {creditBalance} credits
                </div>
                <div className="text-[10px] text-black/40 capitalize">
                  {planInfo?.name || orgPlanType} plan
                </div>
              </div>
            </button>
          )}
        </div>

      </aside>
    </>
  );
}
